import {
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { CustomerCombobox } from "@/components/CustomerCombobox";
import { ActivityPanel } from "@/components/common/ActivityPanel";

export function SerialNumberFormDialog({
  editItem,
  formData,
  setFormData,
  categories = [],
  customers = [],
  saving,
  draftRestored,
  onDiscardDraft,
  onSubmit,
  onCancel,
}) {
  return (
    <DialogContent className={editItem ? "sm:max-w-[640px] max-h-[90vh] overflow-y-auto" : "sm:max-w-[500px]"}>
      <DialogHeader>
        <DialogTitle>{editItem ? "Seriennummer bearbeiten" : "Neue Seriennummer"}</DialogTitle>
        <DialogDescription>Erfassen Sie die Gerätedaten.</DialogDescription>
      </DialogHeader>
      {draftRestored && !editItem && (
        <div
          className="flex items-center justify-between gap-2 rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-800 px-3 py-2 text-sm"
          data-testid="serial-draft-banner"
        >
          <span className="text-amber-800 dark:text-amber-200">Nicht gespeicherter Entwurf wiederhergestellt.</span>
          <Button type="button" variant="ghost" size="sm" onClick={onDiscardDraft} data-testid="serial-discard-draft-btn">
            Verwerfen
          </Button>
        </div>
      )}
      <form onSubmit={onSubmit}>
        <div className="grid gap-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="serial_number">Seriennummer *</Label>
              <Input
                id="serial_number"
                value={formData.serial_number}
                onChange={(e) => setFormData({ ...formData, serial_number: e.target.value })}
                placeholder="SN-123456789"
                required
                className="font-mono"
                data-testid="serial-number-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="category">Kategorie</Label>
              <Select
                value={formData.category}
                onValueChange={(value) => setFormData({ ...formData, category: value })}
              >
                <SelectTrigger data-testid="category-select">
                  <SelectValue placeholder="Auswählen..." />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="product_name">Produktname *</Label>
            <Input
              id="product_name"
              value={formData.product_name}
              onChange={(e) => setFormData({ ...formData, product_name: e.target.value })}
              placeholder="z.B. HP ProDesk 400 G7"
              required
              data-testid="product-name-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="customer_id">Kunde</Label>
            <CustomerCombobox
              customers={customers}
              value={formData.customer_id}
              onChange={(v) => setFormData({ ...formData, customer_id: v })}
              placeholder="Kunde auswählen oder suchen..."
              testid="serial-customer-combobox"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="purchase_date">Kaufdatum</Label>
              <Input
                id="purchase_date"
                type="date"
                value={formData.purchase_date}
                onChange={(e) => setFormData({ ...formData, purchase_date: e.target.value })}
                data-testid="purchase-date-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="expiry_date">Ablaufdatum</Label>
              <Input
                id="expiry_date"
                type="date"
                value={formData.expiry_date}
                onChange={(e) => setFormData({ ...formData, expiry_date: e.target.value })}
                data-testid="expiry-date-input"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="notes">Notizen</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Zusätzliche Informationen..."
              rows={2}
              data-testid="serial-notes-input"
            />
          </div>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onCancel}>Abbrechen</Button>
          <Button type="submit" disabled={saving} data-testid="serial-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : editItem ? "Speichern" : "Anlegen"}
          </Button>
        </DialogFooter>
      </form>
      {editItem && (
        <div className="mt-2 border-t border-border pt-4">
          <ActivityPanel entityType="serial_number" entityId={editItem.id} />
        </div>
      )}
    </DialogContent>
  );
}
